import { Input } from '@/components/ui/input'
import useDebounce from '@/utils/use-debounce'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'

export function Search() {
  const { t } = useTranslation(['global'])
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState<string>(searchParams.get('search') ?? '')
  const debouncedText = useDebounce<string>(search, 500)
  const appliedSearchRef = useRef<string>(debouncedText)
  const previousPathnameRef = useRef(location.pathname)

  // Sync the input to the URL's own `search` param on a page navigation, or
  // on a same-page params-only change that we did not just commit ourselves
  // (e.g. browser Back/Forward popping to a previous `?search=` history
  // entry). Skip when the param already matches what we last applied, so a
  // re-run caused by our own debounced commit does not clobber in-flight
  // typing (the input may already hold a newer, not-yet-debounced value).
  useEffect(() => {
    const urlSearch = searchParams.get('search') ?? ''
    if (
      previousPathnameRef.current === location.pathname &&
      urlSearch === appliedSearchRef.current
    )
      return
    previousPathnameRef.current = location.pathname
    setSearch(urlSearch)
  }, [location.pathname, searchParams])

  useEffect(() => {
    // A navigation alone gives `setSearchParams`/`searchParams` a new
    // identity without the debounced value actually changing — skip that
    // no-op re-run instead of re-applying an already-applied term.
    if (appliedSearchRef.current === debouncedText) return

    // The URL already reflects this debounced value (e.g. it was just
    // synced from the destination page's own param) — record it as applied
    // without pushing a redundant/duplicate navigation.
    if ((searchParams.get('search') ?? '') === debouncedText) {
      appliedSearchRef.current = debouncedText
      return
    }
    appliedSearchRef.current = debouncedText

    if (debouncedText) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev)
        params.set('search', debouncedText)
        params.set('page', '1')
        return params
      })
    } else if (searchParams.get('search')) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev)
        params.delete('search')
        return params
      })
    }
  }, [debouncedText, setSearchParams, searchParams])

  return (
    <Input
      type='search'
      placeholder={t('search')}
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className='w-full md:w-48 lg:w-96'
    />
  )
}
